import { useCallback, useEffect, useState } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import Card, { CardHeader, CardBody } from "components/partials/card/card";
import { Box } from "ui/box";
import { Typography } from "ui/typography";
import { Button, LoadingButton, LoadingButtonState } from "ui/button";
import AlertBox from "components/partials/alert-box/alert-box";
import { Switch } from "ui/switch";
import EmailTypesSendersTable from "containers/admin/clients/client/components/email-types/components/email-types-senders-table/email-types-senders-table";
import EmailTypesSendersForm from "containers/admin/clients/client/components/email-types/components/email-types-senders-form/email-types-senders-form";
import { LoadingSpinner } from "components/partials/loading-screen/loading-screen";
import { Accordion } from "ui/accordion";
import { Tooltip } from "ui/tooltip";

import { ClientAttributes } from "hooks/api/use-clients";
import {
  getEmailTypesRequest,
  useSetDefaultEmailType,
  useTriggerEmailTypeSync,
  useUpdateEmailTypeSenderDetails,
} from "hooks/api/use-email-types";

import { ISenderDetails } from "containers/admin/clients/client/components/email-types/components/email-types-senders-form/email-types-senders-form.types";

import {
  IAlert,
  IEmailType,
  IEmailTypeDetails,
} from "containers/admin/clients/client/components/email-types/email-types.types";

import {
  ALERT_DATA,
  NEW_SENDER_DATA,
} from "containers/admin/clients/client/components/email-types/email-types.constants";

import Icons from "assets";

const EDIT_DETAILS_BUTTON_SX: SxProps<Theme> = { mt: 2 };

const EmailTypesTab = ({ client }: { client: ClientAttributes }) => {
  const [emailTypes, setEmailTypes] = useState<IEmailType[]>([]);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [emailTypesSyncState, setEmailTypesSyncState] = useState<LoadingButtonState>(
    LoadingButtonState.Default,
  );
  const [emailTypesFetchState, setEmailTypesFetchState] = useState<LoadingButtonState>(
    LoadingButtonState.Default,
  );
  const [sendersTableLoading, setSendersTableLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showRefreshButton, setShowRefreshButton] = useState<boolean>(false);
  const [alertData, setAlertData] = useState<IAlert>(ALERT_DATA["default"]);
  const [defaultEmailType, setDefaultEmailType] = useState("");

  const [emailTypeDetailsState, setEmailTypeDetailsState] = useState<IEmailTypeDetails[]>([]);

  const triggerSyncMutation = useTriggerEmailTypeSync();
  const setDefaultMutation = useSetDefaultEmailType(client.id);
  const updateSendersMutation = useUpdateEmailTypeSenderDetails(client.id);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (showAlert) {
      timeout = setTimeout(() => {
        setShowAlert(false);
      }, 8000);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [showAlert]);

  const handleEmailTypesFetch = useCallback(async () => {
    setEmailTypesFetchState(LoadingButtonState.Loading);
    try {
      const emailTypes = (await getEmailTypesRequest(client.id)) as unknown as IEmailType[];

      setEmailTypes(emailTypes);

      setEmailTypeDetailsState(
        emailTypes.map((el: IEmailType) => {
          return { id: el.id, isExpanded: false, isEditModeOn: false };
        }),
      );
      const curentDefaultEmail = emailTypes.find((el: any) => el.defaultForClient);

      setDefaultEmailType(curentDefaultEmail?.id ?? "");
      setEmailTypesFetchState(LoadingButtonState.Success);
    } catch (e) {
      console.error("Error during fetching email types.EmailTypes", e);
      setEmailTypesFetchState(LoadingButtonState.Error);
    }
  }, [client.id]);

  useEffect(() => {
    handleEmailTypesFetch();
  }, [handleEmailTypesFetch]);

  const handleEmailTypesSync = async () => {
    setEmailTypesSyncState(LoadingButtonState.Loading);

    try {
      await triggerSyncMutation.mutateAsync();
      setEmailTypesSyncState(LoadingButtonState.Success);
      setAlertData(ALERT_DATA["success"]);
      setShowAlert(true);
      if (!showRefreshButton) {
        setShowRefreshButton(true);
        setEmailTypesFetchState(LoadingButtonState.Default);
      }
    } catch (e) {
      console.error("Error during syncing email types.handleEmailTypesSync", e);
      setEmailTypesSyncState(LoadingButtonState.Error);
      setAlertData(ALERT_DATA["error"]);
      setShowAlert(true);
    }
  };

  const handleDefaultEmailTypeChange = async (emailTypeId: string) => {
    try {
      const newDefaultEmailType = (await setDefaultMutation.mutateAsync(
        emailTypeId,
      )) as unknown as IEmailType;
      setDefaultEmailType(newDefaultEmailType?.id);
    } catch (e) {
      console.error("Error during setting new email type", e);
      setAlertData(ALERT_DATA["error"]);
      setShowAlert(true);
    }
  };

  const handleEmailTypeSendersSave = async (senders: ISenderDetails[], emailTypeId: string) => {
    setSendersTableLoading(true);

    try {
      const emailDetails = (await updateSendersMutation.mutateAsync({
        emailTypeId,
        senders: senders as unknown as Parameters<
          typeof updateSendersMutation.mutateAsync
        >[0]["senders"],
      })) as unknown as IEmailType;

      const newEmailTypes = emailTypes.map((el) => (el.id === emailDetails.id ? emailDetails : el));
      setEmailTypes(newEmailTypes);
    } catch (e) {
      console.error("Error during setting Email type details", e);
      setAlertData(ALERT_DATA["error"]);
      setShowAlert(true);
    } finally {
      setSendersTableLoading(false);
    }
  };

  const onEditModeClosed = (currState: IEmailTypeDetails) => {
    currState.isEditModeOn = false;

    const newEmailTypeSendersState = emailTypeDetailsState.map((i) => {
      return i.id === currState.id ? currState : i;
    });

    setEmailTypeDetailsState(newEmailTypeSendersState);
  };

  const getSendersTable = (email: IEmailType) => {
    const defaultSender = email.senders.find(
      (sender: ISenderDetails) => sender.defaultForEmailType,
    );

    return !sendersTableLoading ? (
      <EmailTypesSendersTable {...defaultSender} email={email} />
    ) : (
      <div className="flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          gap: { xs: 0, lg: "1.5rem" },
          flexWrap: "wrap",
          mt: "-0.25rem",
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: "2.5rem",
            maxWidth: "630px",
            width: "630px",
          }}
        >
          <div className="flex items-center gap-3">
            <Typography variant="h3" sx={{ color: "secondary.main" }}>
              Email types
            </Typography>
            {showRefreshButton && (
              <Tooltip label="Refresh email types" placement="right">
                <LoadingButton
                  size="large"
                  variant="outlined"
                  onClick={handleEmailTypesFetch}
                  state={emailTypesFetchState}
                >
                  <Icons.Refresh className="rotate-90" />
                </LoadingButton>
              </Tooltip>
            )}
          </div>
          <LoadingButton
            size="large"
            variant="outlined"
            onClick={handleEmailTypesSync}
            loadingMessage="Synchronizing"
            successMessage="Synchronized"
            errorMessage="Error. Try again."
            state={emailTypesSyncState}
          >
            Sync email types
          </LoadingButton>
        </Box>

        {showAlert && (
          <Box sx={{ maxWidth: "630px", width: { xs: "630px", lg: "340px" } }}>
            <AlertBox
              sx={{ maxWidth: "630px", mb: 2 }}
              alert={{
                status: alertData.status,
                title: alertData.title,
                message: alertData.message,
              }}
              showCloseButton={true}
              handleClose={() => setShowAlert(false)}
            />
          </Box>
        )}
      </Box>
      <Box sx={{ maxWidth: "630px" }}>
        {emailTypes.length > 0
          ? emailTypes.map((email: IEmailType) => {
              const currState = { ...emailTypeDetailsState.find((el) => el.id === email.id)! };

              email.senders = email?.senders?.length ? email.senders : [NEW_SENDER_DATA];

              return (
                <Card key={email.id} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      pr: "1rem",
                    }}
                  >
                    <Box>
                      <CardHeader heading={email.externalName} sx={{ pb: 0.75 }} />
                      <CardBody
                        sx={{ pt: 0, color: "grey.500" }}
                      >{`ID: ${email.externalId}`}</CardBody>
                    </Box>
                    <Switch
                      toggleName="Default email type"
                      labelPlacement="end"
                      labelSx={(theme) => ({ color: theme.palette.grey[500] })}
                      checked={
                        defaultEmailType ? defaultEmailType === email.id : email.defaultForClient
                      }
                      onChange={() => {
                        handleDefaultEmailTypeChange(email.id);
                      }}
                    />
                  </Box>
                  <div className="flex relative items-center pb-4">
                    <div className="flex px-4 w-full">
                      <Accordion
                        variant="primary"
                        title="Details"
                        disabled={currState?.isEditModeOn}
                        defaultExpanded={currState?.isExpanded}
                      >
                        {currState?.isEditModeOn ? (
                          <EmailTypesSendersForm
                            onCancel={() => {
                              onEditModeClosed(currState);
                            }}
                            saveSenders={(senders: ISenderDetails[]) => {
                              handleEmailTypeSendersSave(senders, email.id);
                              onEditModeClosed(currState);
                            }}
                            email={email}
                          />
                        ) : (
                          getSendersTable(email)
                        )}
                        {!currState?.isEditModeOn && (
                          <Button
                            data-testid="edit-details-button"
                            size="small"
                            variant="contained"
                            sx={EDIT_DETAILS_BUTTON_SX}
                            startIcon={<Icons.Edit />}
                            onClick={() => {
                              currState.isEditModeOn = true;

                              const newEmailTypeSendersState = emailTypeDetailsState.map((i) => {
                                return i.id === currState.id
                                  ? currState
                                  : { ...i, isEditModeOn: false };
                              });

                              setEmailTypeDetailsState(newEmailTypeSendersState);
                            }}
                          >
                            Edit senders
                          </Button>
                        )}
                      </Accordion>
                    </div>
                  </div>
                </Card>
              );
            })
          : !loading && (
              <Card sx={{ p: 3.5 }}>
                <Typography
                  variant="body1"
                  sx={{ textAlign: "center", fontSize: "20px", fontWeight: 600 }}>
                  There are no Email Types created yet.
                </Typography>
              </Card>
            )}
      </Box>
    </Box>
  );
};

export default EmailTypesTab;
